(() => {
  const REVEAL_SELECTOR = '.kivo-home-reveal';
  const QUEUED_CLASS = 'is-kivo-home-reveal-queued';
  const VISIBLE_CLASS = 'is-kivo-home-reveal-visible';
  const COMPLETE_CLASS = 'is-kivo-home-reveal-complete';
  const BOUND_ATTR = 'data-kivo-home-reveal-bound';
  const REVEAL_INTERVAL = 160;
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const ANCHOR_SCROLL_SELECTOR = 'a[data-kivo-home-anchor-scroll]';

  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  let observer = null;
  let queue = [];
  let queueTimer = null;
  let lastRevealAt = -REVEAL_INTERVAL;

  function prefersReducedMotion() {
    return reducedMotion.matches;
  }

  function revealImmediately(element) {
    element.classList.remove(QUEUED_CLASS);
    element.classList.add(VISIBLE_CLASS, COMPLETE_CLASS);
  }

  function markCompleteWhenDone(element) {
    const fallbackTimer = window.setTimeout(() => {
      element.classList.add(COMPLETE_CLASS);
    }, 900);

    const onAnimationEnd = (event) => {
      if (event.target !== element) return;

      window.clearTimeout(fallbackTimer);
      element.classList.add(COMPLETE_CLASS);
      element.removeEventListener('animationend', onAnimationEnd);
    };

    element.addEventListener('animationend', onAnimationEnd);
  }

  function compareDocumentOrder(a, b) {
    if (a === b) return 0;

    const position = a.compareDocumentPosition(b);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  }

  function scheduleQueue() {
    if (queueTimer || queue.length === 0) return;

    const elapsed = performance.now() - lastRevealAt;
    const delay = Math.max(0, REVEAL_INTERVAL - elapsed);

    queueTimer = window.setTimeout(() => {
      queueTimer = null;

      const element = queue.shift();
      if (!element || !document.documentElement.contains(element)) {
        scheduleQueue();
        return;
      }

      element.classList.remove(QUEUED_CLASS);
      element.classList.add(VISIBLE_CLASS);
      markCompleteWhenDone(element);
      lastRevealAt = performance.now();

      scheduleQueue();
    }, delay);
  }

  function queueReveal(element) {
    if (element.classList.contains(VISIBLE_CLASS) || element.classList.contains(QUEUED_CLASS)) return;

    element.classList.add(QUEUED_CLASS);
    queue.push(element);
    queue.sort(compareDocumentOrder);
    scheduleQueue();
  }

  function onIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting && entry.intersectionRatio <= 0) return;

      observer.unobserve(entry.target);
      queueReveal(entry.target);
    });
  }

  function getRevealElements(root) {
    const scope = root || document;
    const scopedElements = Array.from(scope.querySelectorAll(REVEAL_SELECTOR));

    if (scope.matches && scope.matches(REVEAL_SELECTOR)) {
      scopedElements.unshift(scope);
    }

    return scopedElements;
  }

  function revealAll(root) {
    getRevealElements(root).forEach(revealImmediately);
  }

  function initializeKivoHomeReveal(root = document) {
    const elements = getRevealElements(root).filter((element) => element.getAttribute(BOUND_ATTR) !== 'true');
    if (elements.length === 0) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window) || (window.Shopify && Shopify.designMode)) {
      elements.forEach((element) => {
        element.setAttribute(BOUND_ATTR, 'true');
        revealImmediately(element);
      });
      return;
    }

    observer = observer || new IntersectionObserver(onIntersection, {
      rootMargin: '0px',
      threshold: 0.01,
    });

    elements.forEach((element) => {
      element.setAttribute(BOUND_ATTR, 'true');
      observer.observe(element);
    });
  }

  function getSamePageAnchorTarget(anchor) {
    if (!anchor.hash || anchor.hash === '#') return null;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (error) {
      return null;
    }

    if (
      url.origin !== window.location.origin ||
      url.pathname.replace(/\/$/, '') !== window.location.pathname.replace(/\/$/, '') ||
      url.search !== window.location.search
    ) {
      return null;
    }

    let id = url.hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (error) {
      return null;
    }

    return document.getElementById(id);
  }

  function onAnchorScrollClick(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      prefersReducedMotion()
    ) {
      return;
    }

    const anchor = event.target.closest(ANCHOR_SCROLL_SELECTOR);
    if (!anchor) return;

    const target = getSamePageAnchorTarget(anchor);
    if (!target) return;

    event.preventDefault();

    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      target.scrollIntoView();
    }

    if (window.history && window.history.pushState && anchor.hash !== window.location.hash) {
      window.history.pushState(null, '', anchor.hash);
    }
  }

  document.addEventListener('click', onAnchorScrollClick);

  function initializeAfterPageLoad() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => initializeKivoHomeReveal());
    });
  }

  if (document.readyState === 'complete') {
    initializeAfterPageLoad();
  } else {
    window.addEventListener('load', initializeAfterPageLoad, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeKivoHomeReveal(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    queue = queue.filter((element) => !event.target.contains(element));
  });

  function onReducedMotionChange(event) {
    if (!event.matches) return;

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    revealAll();
    queue = [];
    window.clearTimeout(queueTimer);
    queueTimer = null;
  }

  if (reducedMotion.addEventListener) {
    reducedMotion.addEventListener('change', onReducedMotionChange);
  } else if (reducedMotion.addListener) {
    reducedMotion.addListener(onReducedMotionChange);
  }
})();
